import type { GeckoColorScale } from 'color-scale-kit'
import type {
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentRect,
  GeckoConeCourse,
  GeckoLegacyBoilerWall,
  GeckoMeasurementUnit,
  GeckoShellCourse,
} from 'types'

export enum AxisTickMode {
  Nice = 'nice',
}

export enum ReportAxisMode {
  Embedded = 'embedded',
  None = 'none',
}

export enum ReportComponentOverlayMode {
  Standard = 'standard',
  Full = 'full',
}

export enum ReportOverlayMode {
  Embedded = 'embedded',
  None = 'none',
}

export enum ReportLabelMode {
  All = 'all',
  AutoThrottle = 'auto-throttle',
  None = 'none',
}

export enum OverlayLabelAnchor {
  Center = 'center',
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right',
}

export enum OverlayLabelPlacement {
  Anchored = 'anchored',
  CourseGutter = 'course-gutter',
  ShellCourseList = 'shell-course-list',
}

export interface ReportHeatmapRequest {
  kind: 'section' | 'overview'
  componentId: string
  sectionKey?: string
  cdfId: string
  legendTitle?: string
  cdf: GeckoComponentDataFrame
  componentModel: GeckoComponentModel
  bounds: GeckoComponentRect
  colorThreshold: GeckoColorScaleThreshold | undefined
  /**
   * Unit for the legend value scale (ticks + unit label) and heatmap values.
   * Defaults to `resolveValueDisplayUnit(cdf measurement unit)` (inches for
   * distance units) when omitted. May be a non-distance unit (e.g. percent for
   * difference-from-nominal % views).
   */
  valueDisplayUnit?: GeckoMeasurementUnit
  /**
   * Unit for nominal-thickness overlay labels (e.g. shell/cone course nominal).
   * Thickness is always a length, so this is kept separate from
   * `valueDisplayUnit` (which can be percent for difference-from-nominal %
   * views). Defaults to each measurement's own unit (inches) when omitted.
   */
  nominalDisplayUnit?: GeckoMeasurementUnit
  includeComponentOverlay?: boolean
  includeSectionLabels?: boolean
  sectionLabelBounds?: GeckoComponentRect
  componentOverlayMode?: ReportComponentOverlayMode
  includeWeldOverlays?: boolean
  includeAxes: boolean
  axisMetadata?: ReportHeatmapRequestAxisMetadata
  includeHeatmap?: boolean
  includeLegend: boolean
  includeOverlay: boolean
  overlayMode: ReportOverlayMode
  overlayPrimitives?: OverlayPrimitives
  overlaySignature?: string
  labelMode: ReportLabelMode
  outputWidthPx: number
  background: 'white'
  rawValues: Readonly<Float32Array>
  rawValuesXBinCount?: number
}

export interface ReportHeatmapDescriptor {
  version: 7
  hash: string
  kind: 'section' | 'overview'
  componentId: string
  sectionKey?: string
  cdfId: string
  legendTitle?: string
  bounds: {
    xStart: number
    xEnd: number
    xUnit: string
    yStart: number
    yEnd: number
    yUnit: string
  }
  threshold?: {
    min: number
    mid: number
    max: number
    unit: string
  }
  outputWidthPx: number
  axisMode: ReportAxisMode
  axisMetadata?: ReportHeatmapAxisMetadataDescriptor
  boilerTubeNumbering?: ReportHeatmapDescriptorBoilerTubeWall[]
  axisTickMode: AxisTickMode
  composite?: ReportHeatmapCompositeDescriptor
  heatmapMode?: ReportOverlayMode.None
  legendMode: 'separate'
  componentOverlayMode: ReportComponentOverlayMode | ReportOverlayMode.None
  includeSectionLabels: boolean
  includeWeldOverlays: boolean
  overlaySignature?: string
  // Unit for nominal-thickness overlay labels. Included so toggling in↔mm
  // invalidates the cache even when the value-scale unit is unchanged (e.g.
  // percent on difference-from-nominal % views).
  nominalDisplayUnit?: string
  overlayMode: ReportOverlayMode
  labelMode: ReportLabelMode
  background: 'white'
}

export interface ReportHeatmapDescriptorBoilerTubeWall {
  xStart: number
  xEnd: number
  customerTubeEnd?: number
  customerTubeInverted: boolean
}

export interface ReportHeatmapAxis {
  name?: string
  displayUnit?: GeckoMeasurementUnit
}

export interface ReportHeatmapRequestAxisMetadata {
  x?: ReportHeatmapAxis
  y?: ReportHeatmapAxis
}

export interface ReportHeatmapAxisMetadataDescriptor {
  x?: { name?: string; displayUnit?: string }
  y?: { name?: string; displayUnit?: string }
}

export interface ReportHeatmapCompositeSliceDescriptor {
  index: number
  bounds: ReportHeatmapDescriptor['bounds']
}

export interface ReportHeatmapCompositeDescriptor {
  axis: 'x' | 'y'
  layout: 'stacked' | 'side-by-side'
  slices: ReportHeatmapCompositeSliceDescriptor[]
}

export interface ReportHeatmapBundle {
  heatmapBlob: Blob
  legendBlob: Blob
  descriptor: ReportHeatmapDescriptor
}

export type NormalizedBounds = {
  rect: GeckoComponentRect
  xStart: number
  xEnd: number
  yStart: number
  yEnd: number
}

export type CroppedValues = {
  values: Float32Array
  xBinCount: number
  yBinCount: number
  bounds: NormalizedBounds
}

export type ThresholdSignature = {
  min: number
  mid: number
  max: number
  unit: GeckoMeasurementUnit
}

export type ColorScaleInfo = {
  colorScale: GeckoColorScale
  min: number
  mid: number
  max: number
  measurementUnit: GeckoMeasurementUnit
}

export type DisplayAxisBounds = {
  start: number
  end: number
  unit: GeckoMeasurementUnit
}

export type DisplayBounds = {
  x: DisplayAxisBounds
  y: DisplayAxisBounds
}

export type PlotMetrics = {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
  gutterLeft: number
  gutterRight: number
  bottomPadding?: number
  axisFontSize?: number
}

export type OverlayLinePrimitive = {
  x1: number
  y1: number
  x2: number
  y2: number
  strokeStyle: string
  lineWidth: number
}

export type OverlayShapePrimitive =
  | {
      kind: 'circle'
      x: number
      y: number
      radiusX: number
      radiusY: number
      strokeStyle: string
      lineWidth: number
    }
  | {
      kind: 'rectangle'
      x: number
      y: number
      width: number
      height: number
      strokeStyle: string
      lineWidth: number
    }

export type OverlayLabelPrimitive = {
  x: number
  y: number
  lines: string[]
  anchor: OverlayLabelAnchor
  placement: OverlayLabelPlacement
  slotStartY?: number
  slotEndY?: number
  priority: number
  font: string
  lineHeight: number
}

export type OverlayPrimitives = {
  linePrimitives: OverlayLinePrimitive[]
  shapePrimitives: OverlayShapePrimitive[]
  labelPrimitives: OverlayLabelPrimitive[]
}

export type LabelBox = {
  left: number
  top: number
  width: number
  height: number
}

export type LabelDimensions = {
  width: number
  height: number
}

export type PositionedLabel = {
  label: OverlayLabelPrimitive
  box: LabelBox
}

export type PositionedTextLabel = {
  label: OverlayLabelPrimitive
  point: {
    x: number
    y: number
  }
  font: string
  lineHeight: number
}

export type CourseWithThickness = GeckoShellCourse | GeckoConeCourse
export type BoilerWall = GeckoLegacyBoilerWall
